export const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString();
};

export const formatCurrency = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '$0.00';
  return '$' + num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatPercentage = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return '0%';
  return `${num.toFixed(1)}%`;
};

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  // Pin to America/New_York so grouping matches what admins see regardless of browser TZ.
  return new Date(dateString).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
};

// Today's calendar date in America/New_York, as { year, month (1-12), day }.
// Used for filter math so quick-select ranges align with how broadcasts are displayed.
export const etTodayParts = (): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type: string) => Number(parts.find(p => p.type === type)!.value);
  return { year: get('year'), month: get('month'), day: get('day') };
};

// Table row dates (formatDate output, e.g. "10/16/2026") -> "2026-10-16".
export const displayDateToYMD = (display: string): string => {
  const [m, d, y] = display.split('/');
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

// A cost cap's date range for display: "Oct 16" or "Oct 1 – Oct 31", with the
// year added when the range isn't in the current year.
export const formatCapRange = (startYMD: string, endYMD: string): string => {
  const fmt = (ymd: string, withYear: boolean) =>
    new Date(`${ymd}T00:00:00Z`).toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
    });
  const withYear = endYMD.slice(0, 4) !== String(etTodayParts().year);
  return startYMD === endYMD
    ? fmt(startYMD, withYear)
    : `${fmt(startYMD, false)} – ${fmt(endYMD, withYear)}`;
};

// Formats a Date's calendar fields as YYYY-MM-DD (no timezone conversion).
export const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const UPPERCASE_WORDS = new Set(['cbcpac', 'pac', 'nh', 'ct', 'nj', 'hq', 'edf', 'usa', 'crew']);
const ACCOUNT_DISPLAY_NAMES: Record<string, string> = {
  'ar_annie_andrews_for_senate_trueblue': 'Dr. Annie Andrews For Senate',
};

export const formatAccountName = (account: string): string => {
  if (ACCOUNT_DISPLAY_NAMES[account]) return ACCOUNT_DISPLAY_NAMES[account];
  return account
    .replace(/_?true_?blue$/, '')
    .replace(/_?trueblue$/, '')
    .replace(/_?tba$/, '')
    .replace(/_?combined$/, '')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => UPPERCASE_WORDS.has(word.toLowerCase())
      ? word.toUpperCase()
      : word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
